import { covertToArray, toggleArrayValue } from "~/lib/helpers";
import { useOnObjectChanged } from "~/lib/useOnObjectChanged";
import { useMutation, useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { Dispatch, useReducer } from "react";
import { apiClient } from "..";
import { PaginatedQueryParams, PaginatedResponse } from "../types";
import { Frequency } from "../types/frequency";
import { ImageMedia, Media } from "./Media";
import { Tag, TagFilterParams } from "./Tags";
import {
  fetchPaginatedQueryFunction,
  fetchQueryFunction,
  makeQueryKey,
  PaginatedQueryFunction,
  useInfiniteQuery,
  UseInfiniteQueryFunction,
  usePaginatedQuery,
  UsePaginatedQueryFunction,
} from "./queries";

export type Location = {
  id: number;
  name: string;
  street_address: string;
  city: string;
  province: string;
  postal_code: string;
  country: string;
  address?: string;
  full_address?: string;
  email?: string;
  phone_number?: string;
  website?: string;
  website2?: string;
  lonlat?: string;
  bounds?: string;
  listing_id?: number;
  listing_slug?: string;
};

export type RRule = {
  count?: number;
  freq: Frequency;
  interval: number;
  until: string;
};

export type EventTime = {
  id: number;
  name: string;
  start: string;
  end: string;
  all_day: boolean;
  sold_out: boolean;
  rrule?: RRule;
  locations: Location[];
  timezone: string;
};

export type BriefEvent = {
  id: number;
  uuid: string;
  slug: string;
  name: string;
  featured: boolean;
  status: string;
  region_id: number;
  region_name: string;
  event_time_id: number;
  key: string;
  start: string;
  end: string;
  locations: Location[];
  rrule?: RRule;
  tags: Tag[];
  thumbnail_media?: ImageMedia;
  lead_media?: Media;
  albumable_names: string[];
  album_media: Media[];
  bounds: string;
  lonlat: string;
  centroid: string;
  deltas: string[];
  timezone: string;
  description?: string;
  short_description?: string;
};

export type Event = {
  id: number;
  slug: string;
  uuid: string;
  name: string;
  featured: boolean;
  ticket_url?: string;
  website?: string;
  status: string;
  phone_number?: string;
  description?: string;
  short_description?: string;
  region_id: number;
  region_name?: string;
  cost?: string;
  tags?: Tag[];
  thumbnail_media?: ImageMedia;
  lead_media?: Media;
  albumable_names?: string[];
  album_media?: Media[];
  bounds: string;
  lonlat: string;
  centroid: string;
  deltas: {
    lat: number;
    lon: number;
  };
  event_times: EventTime[];
};

export type EventResponse = PaginatedResponse<BriefEvent>;

export type EventSortOptionKeys = "featured" | "alpha";
export const EventSortOptions: { [key in EventSortOptionKeys]: string } = {
  featured: "Featured",
  alpha: "Name (A-Z)",
};

export type EventQueryParams = PaginatedQueryParams & {
  sort?: EventSortOptionKeys;
  region_ids?: number[] | null;
  search?: string;
  uniq_events?: boolean;
  filter?: EventFilters;
  excluded_id?: number | number[];
};

export type EventFilters = {
  id?: number | number[];
  featured?: boolean;
  location_id?: number;
  listing_id?: number;
  tags?: TagFilterParams;
  region_id?: number | number[];
  start_gte?: string; // Start date greater than or equal to. Accepts Date or DateTime parsible string
  start_lte?: string; // Start date less than or equal to. Accepts Date or DateTime parsible string
  end_gte?: string; // End date greater than or equal to. Accepts Date or DateTime parsible string
  end_lte?: string; // End date less than or equal to. Accepts Date or DateTime parsible string
};

export const fetchEvent = (id: number | string) =>
  fetchQueryFunction<void, Event>({
    path: `events/${id}`,
    resource: "events",
    id,
  });

export const useEvent = (id: number | string) =>
  useQuery({
    queryKey: makeQueryKey({ resource: "events", id }),
    queryFn: () => fetchEvent(id),
  });

export const fetchEvents: PaginatedQueryFunction<
  EventQueryParams,
  EventResponse
> = (params, preload) =>
  fetchPaginatedQueryFunction({
    path: "events",
    resource: "events",
    params,
    preload,
  });

export const useEvents: UsePaginatedQueryFunction<
  BriefEvent,
  EventQueryParams
> = (params, options) =>
  usePaginatedQuery("events", fetchEvents, params, options);

export const useInfiniteEvents: UseInfiniteQueryFunction<
  BriefEvent,
  EventQueryParams
> = (params, options) =>
  useInfiniteQuery("events", fetchEvents, params, options);

export type CreateEventData = {
  event: {
    name: string;
    region_id: number;
    description: string;
    lead_media: Media;
    phone_number?: string;
    website?: string;
    email: string;
    ticket_url?: string;
    cost?: string;
    event_times_attributes: Array<{
      start: string;
      end: string;
      rrule?: {
        recurring: boolean;
        freq?: keyof typeof Frequency;
        interval?: number;
        until?: string;
      };
      location_id?: number;
      locations_attributes?: Array<{
        name: string;
        street_address: string;
        city: string;
        province: string;
        country: string;
        postal_code: string;
        lonlat?: string;
        phone_number?: string;
        website?: string;
      }>;
    }>;
  };
  recaptcha_token?: string;
};

export const createEvent = async (data: CreateEventData) => {
  return apiClient()
    .post<Event>("events", data)
    .then(({ data }) => data);
};

export const useCreateEvent = () => {
  return useMutation<Event, Error, CreateEventData>({
    mutationFn: createEvent,
    onError: (error) => {
      if (process.env.NODE_ENV === "development") console.error(error);
    },
  });
};

export type EventFilterAction =
  | { type: "toggleTagId"; payload: number }
  | { type: "setRegionId"; payload: number }
  | { type: "clearValue"; payload: "region_id" | "tags" | "dates" }
  | { type: "setSearch"; payload?: string }
  | { type: "setTagIds"; payload?: number[] }
  | { type: "setDateRange"; payload: { start?: string; end?: string } | null }
  | { type: "toggleRecurringEvents"; payload: boolean }
  | { type: "reset"; payload?: EventQueryParams };

export type EventFilterReducer = Dispatch<NonNullable<EventFilterAction>>;

export const useEventFilterParams = (defaultParams: EventQueryParams = {}) => {
  const reducer = useReducer(
    (
      state: EventQueryParams,
      { type, payload }: NonNullable<EventFilterAction>,
    ) => {
      switch (type) {
        case "setRegionId":
          const currentRegionId = covertToArray(state.filter?.region_id);
          return {
            ...state,
            filter: {
              ...state.filter,
              region_id: toggleArrayValue(currentRegionId, payload),
            },
          };
        case "toggleTagId":
          const currentTagId = covertToArray(state.filter?.tags?.id);

          return {
            ...state,
            filter: {
              ...state.filter,
              tags: { id: toggleArrayValue(currentTagId, payload) },
            },
          };
        case "setTagIds":
          return {
            ...state,
            filter: {
              ...state.filter,
              tags: {
                ...defaultParams.filter?.tags,
                id: [
                  ...covertToArray(defaultParams.filter?.tags?.id),
                  ...covertToArray(payload),
                ],
              },
            },
          };
        case "clearValue":
          if (!state.filter) return { ...state };
          if (payload === "dates") {
            const { start_gte, start_lte, end_gte, end_lte, ...newFilter } =
              state.filter;
            return {
              ...state,
              filter: { ...newFilter, ...defaultParams.filter },
            };
          }
          const { [payload]: existingValue, ...newFilter } = state.filter;
          return {
            ...state,
            filter: { ...newFilter, ...defaultParams.filter },
          };

        case "setDateRange":
          if (!payload) {
            // Clear date filters
            const { start_gte, start_lte, end_gte, end_lte, ...newFilter } =
              state.filter || {};
            return {
              ...state,
              filter: { ...newFilter, ...defaultParams.filter },
            };
          }

          const { start, end } = payload;
          let dateFilter: Partial<EventFilters> = {};

          if (start && !end) {
            // Single date selected: start_gte = 12:00 AM of selected date, end_lte = 12:00 AM of next day
            const startDate = dayjs(start).startOf("day");
            const endDate = startDate.add(1, "day").startOf("day");
            dateFilter = {
              start_gte: startDate.toISOString(),
              end_lte: endDate.toISOString(),
            };
          } else if (start && end) {
            // Date range selected: start_gte = 12:00 AM of first date, end_lte = 12:00 AM of day after last date
            const startDate = dayjs(start).startOf("day");
            const endDate = dayjs(end).add(1, "day").startOf("day");
            dateFilter = {
              start_gte: startDate.toISOString(),
              end_lte: endDate.toISOString(),
            };
          }

          return {
            ...state,
            filter: {
              ...state.filter,
              ...dateFilter,
            },
          };

        case "setSearch":
          return { ...state, search: payload };
        case "toggleRecurringEvents":
          return {
            ...state,
            uniq_events: payload,
          };
        case "reset":
          return { ...payload };
        default:
          return { ...state };
      }
    },
    defaultParams,
  );

  // When navigating to a new page in the same route, reset the filter params.
  useOnObjectChanged(defaultParams, (params) => {
    reducer[1]({ type: "reset", payload: params });
  });
  return reducer;
};
